import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { PageTitle, Card } from '../components/common';
import BASE_URL from '../services/baseURL';
import { getImageUrl } from '../services/imagePathConfig';

interface Alumni {
    id: number;
    traineeToken: string;
    name: string;
    role: string;
    location: string;
    profilePicture: string | null;
    linkedin?: string;
    github?: string;
}

const getInitials = (name: string) => {
    return name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
};

const avatarColors = [
    'bg-red-500', 'bg-blue-500', 'bg-green-500', 'bg-purple-500',
    'bg-yellow-500', 'bg-pink-500', 'bg-indigo-500', 'bg-teal-500',
];

const getAvatarColor = (name: string) => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return avatarColors[Math.abs(hash) % avatarColors.length];
};

const ensureAbsoluteUrl = (url: string) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return `https://${url}`;
};

const AlumniCard: React.FC<{ alumni: Alumni; onClick: () => void }> = ({ alumni, onClick }) => (
    <Card className="p-6 flex flex-col items-center text-center cursor-pointer hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 h-[300px] group" onClick={onClick}>
        <div className="flex flex-col items-center w-full grow justify-center">
            <div className="mb-4 relative">
                {alumni.profilePicture ? (
                    <img src={alumni.profilePicture} alt={alumni.name} className="w-28 h-28 rounded-xl object-cover shadow-lg border-2 border-white group-hover:border-red-100 transition-colors" />
                ) : (
                    <div className={`w-28 h-28 rounded-xl flex items-center justify-center shadow-lg border-2 border-white group-hover:border-red-100 transition-colors ${getAvatarColor(alumni.name)}`}>
                        <span className="text-white text-3xl font-bold">{getInitials(alumni.name)}</span>
                    </div>
                )}
            </div>
            <div className="w-full">
                <h3 className="text-lg font-bold text-gray-900 line-clamp-1 mb-1">{alumni.name}</h3>
                <p className="text-xs font-medium text-red-600 mb-2 uppercase tracking-wide px-2 py-0.5 ">{alumni.role || '-'}</p>
                <div className="flex items-center justify-center text-gray-500 text-xs mt-1">
                    <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {alumni.location || '-'}
                </div>
            </div>
        </div>
    </Card>
);

const AlumniPage: React.FC = () => {
    const navigate = useNavigate();
    const [search, setSearch] = useState('');
    const [alumniList, setAlumniList] = useState<Alumni[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchAlumni();
    }, []);

    const fetchAlumni = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await axios.get(`${BASE_URL}/alumniee/getAll`);
            const data = response.data.alumniees || [];
            setAlumniList(
                data.map((a: any) => ({
                    id: a.id,
                    traineeToken: a.traineeToken || '',
                    name: a.fullName || '',
                    role: a.description || '',
                    location: a.location || '',
                    profilePicture: getImageUrl(a.profile) || null,
                    linkedin: ensureAbsoluteUrl(a.linkedin || ''),
                    github: ensureAbsoluteUrl(a.github || ''),
                }))
            );
        } catch (err: any) {
            setError(err.message || 'Failed to fetch alumni');
        } finally {
            setLoading(false);
        }
    };

    const filteredAlumni = useMemo(() => {
        if (!search) return alumniList;
        const q = search.toLowerCase();
        return alumniList.filter(alumni =>
            alumni.name.toLowerCase().includes(q) ||
            alumni.role.toLowerCase().includes(q) ||
            alumni.location.toLowerCase().includes(q)
        );
    }, [alumniList, search]);

    return (
        <div>
            <PageTitle title="Alumni">
                <input
                    type="text"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search by name, role, or location..."
                    className="w-64 px-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-300 focus:border-red-400 mr-auto -ml-[50px]"
                />
            </PageTitle>

            {loading ? (
                <div className="flex justify-center py-16">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-600"></div>
                </div>
            ) : error ? (
                <div className="text-center py-16">
                    <p className="text-red-500">{error}</p>
                    <button onClick={fetchAlumni} className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
                        Retry
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 mt-6">
                    {filteredAlumni.map(alumni => (
                        <AlumniCard 
                            key={alumni.id} 
                            alumni={alumni} 
                            onClick={() => {
                                const baseUrl = window.location.hostname === 'localhost' 
                                    ? 'http://localhost:5173' 
                                    : 'https://alumni.edjamon.com';
                                window.open(`${baseUrl}/?token=${alumni.traineeToken}`, '_blank');
                            }} 
                        />
                    ))}
                    {filteredAlumni.length === 0 && (
                        <p className="col-span-full text-center text-gray-500 py-10">No alumni found matching your search.</p>
                    )}
                </div>
            )}
        </div>
    );
};

export default AlumniPage;
